<script setup lang="ts">
import type { RevalidationSubmitModalTableRow, SituationAnswer } from '../revalidationsShared.types'
import { SolAlert, SolDivider } from '@solfacil/girassol'
import RevalidationAuthBox from './RevalidationAuthBox.vue'
import RevalidationYesNoToggle from './RevalidationYesNoToggle.vue'

const props = defineProps<{
  submitContextAttr: string
  monitoringPlantQuestion: string
  acceptanceLabel: string
  tableRows: RevalidationSubmitModalTableRow[]
  showValidationError?: boolean
}>()

const installationFullyComplete = defineModel<SituationAnswer>('installationFullyComplete', { required: true })
const monitoringPlantRegistered = defineModel<SituationAnswer>('monitoringPlantRegistered', { required: true })
const recentEnergyGeneration = defineModel<SituationAnswer>('recentEnergyGeneration', { required: true })
const acceptanceConfirmed = defineModel<boolean>('acceptanceConfirmed', { required: true })

const VALIDATION_ERROR_TEXT = 'Por favor, responda todas as perguntas com "Sim" e marque a autorização antes de continuar.'

watch(() => props.showValidationError, (val) => {
  if (val) {
    nextTick(() => {
      document.getElementById('revalidation-step1-validation-error')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }
})

function onAcceptanceChange() {
  acceptanceConfirmed.value = !acceptanceConfirmed.value
}
</script>

<template>
  <div
    class="revalidation-submit-modal-body"
    :data-submit-context="submitContextAttr"
  >
    <SolAlert
      v-if="showValidationError"
      id="revalidation-step1-validation-error"
      title="Campos obrigatórios"
      :text="VALIDATION_ERROR_TEXT"
      feedback="error"
    />

    <p class="revalidation-submit-modal-subtitle">
      Antes de continuar, por favor, informe a situação da instalação selecionada:
    </p>

    <div class="revalidation-submit-modal-situation-box" role="group" aria-label="Situação da instalação">
      <RevalidationYesNoToggle
        v-model="installationFullyComplete"
        question-id="revalidation-q-installation"
        question="A instalação foi totalmente concluída?"
      />
      <RevalidationYesNoToggle
        v-model="monitoringPlantRegistered"
        question-id="revalidation-q-monitoring"
        :question="monitoringPlantQuestion"
      />
      <RevalidationYesNoToggle
        v-model="recentEnergyGeneration"
        question-id="revalidation-q-generation"
        question="Houve geração de energia recente nesta instalação?"
      />
    </div>

    <SolDivider
      thickness="x-small"
      orientation="horizontal"
      class="revalidation-submit-modal-section-divider"
    />
    <p class="revalidation-submit-modal-data-intro">
      Estes são os dados da revalidação que será enviada para análise:
    </p>

    <div class="revalidation-submit-modal-table-wrap">
      <table class="revalidation-submit-modal-table">
        <thead>
          <tr>
            <th scope="col" class="revalidation-submit-modal-table-th">
              Nome do projeto
            </th>
            <th scope="col" class="revalidation-submit-modal-table-th">
              Marca do inversor
            </th>
            <th scope="col" class="revalidation-submit-modal-table-th">
              Números de série informados
            </th>
            <th scope="col" class="revalidation-submit-modal-table-th">
              Nome da planta informada
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="tableRows.length">
            <tr v-for="row in tableRows" :key="row.rowKey">
              <td class="revalidation-submit-modal-table-td">
                {{ row.projectName }}
              </td>
              <td class="revalidation-submit-modal-table-td">
                {{ row.inverterBrandLabel }}
              </td>
              <td class="revalidation-submit-modal-table-td">
                <ul v-if="row.serials.length" class="revalidation-submit-modal-serial-list">
                  <li v-for="serial in row.serials" :key="`${row.rowKey}-${serial}`">
                    {{ serial }}
                  </li>
                </ul>
                <span v-else class="revalidation-submit-modal-cell-empty">—</span>
              </td>
              <td class="revalidation-submit-modal-table-td">
                {{ row.plantName }}
              </td>
            </tr>
          </template>
          <tr v-else>
            <td colspan="4" class="revalidation-submit-modal-table-td revalidation-submit-modal-table-empty">
              Nenhum item para exibir.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <RevalidationAuthBox
      id="revalidation-submit-acceptance"
      name="revalidation-submit-acceptance"
      value="acceptance"
      :checked="acceptanceConfirmed"
      @change="onAcceptanceChange"
    >
      {{ acceptanceLabel }}
    </RevalidationAuthBox>
  </div>
</template>

<style scoped lang="scss">
.revalidation-submit-modal-body {
  @apply flex flex-col gap-6;

  .revalidation-submit-modal-subtitle {
    margin: 0;
    @apply fonts-body-large-regular text-neutral-low-pure leading-[1.625];
  }

  .revalidation-submit-modal-situation-box {
    @apply rounded-md overflow-hidden border border-neutral-high-medium bg-neutral-high-pure;

    :deep(.revalidation-yes-no-toggle) {
      @apply border-b border-neutral-high-medium;

      &:last-child {
        @apply border-b-0;
      }
    }
  }

  .revalidation-submit-modal-section-divider {
    @apply w-full flex-shrink-0;
  }

  .revalidation-submit-modal-data-intro {
    margin: 0;
    @apply fonts-body-large-regular text-neutral-low-pure leading-[1.625];
  }

  .revalidation-submit-modal-table-wrap {
    @apply w-full overflow-x-auto;
    @apply rounded-md border border-neutral-high-medium bg-neutral-high-pure;

    .revalidation-submit-modal-table {
      @apply w-full border-collapse text-left;

      .revalidation-submit-modal-table-th {
        @apply fonts-body-medium-bold text-neutral-low-pure;
        @apply px-4 py-4 align-middle;
        @apply border border-neutral-high-medium bg-neutral-high-light;
      }

      .revalidation-submit-modal-table-td {
        @apply fonts-body-medium-regular text-neutral-low-pure;
        @apply px-4 py-4 align-top;
        @apply border border-neutral-high-medium;
      }

      .revalidation-submit-modal-serial-list {
        margin: 0;
        padding-left: 1.25rem;
        @apply list-disc fonts-body-medium-regular text-neutral-low-pure;
      }

      .revalidation-submit-modal-cell-empty,
      .revalidation-submit-modal-table-empty {
        @apply text-neutral-low-light;
      }

      .revalidation-submit-modal-table-empty {
        @apply fonts-body-medium-regular text-center py-6 px-4;
      }
    }
  }
}
</style>
